// The graphics tier (art/model-integration.md Part 9). Tier 1 is the classic
// look: flat Lambert light, the palette sky with its round sun (sun.js, which
// both tiers share), linear haze. Tier 2 — the "lit diorama" — is a desktop
// opt-in (SETTINGS → High-fidelity graphics, or ?gfx=2): film
// tone mapping, a scattered sky whose sun rides the same day cycle, haze that
// takes the horizon's own colour, a sun shadow round the camera, and a bloom
// only the brightest things reach (gfx2.js, loaded only when asked for).
//
// Phones and tablets stay on tier 1 whatever is saved: every pass is battery,
// and the classic look was tuned for them.

const param = (name) => {
  try {
    return new URLSearchParams(location.search).get(name);
  } catch {
    return null;
  }
};

const readTier = () => {
  try {
    const q = param("gfx");
    if (q === "1" || q === "2") return Number(q);
    if (matchMedia("(pointer: coarse)").matches) return 1;
    return localStorage.getItem("va-gfx") === "2" ? 2 : 1;
  } catch {
    return 1; // storage blocked (private window, embed): the classic look
  }
};

/** 1 (classic) or 2 (lit diorama), fixed for the life of the page. */
export const GFX = typeof document === "undefined" ? 1 : readTier();

// An explicit ?gfx= is someone who knows their machine (a review, the
// screenshot director): it holds even on a software rasterizer, where the
// saved preference steps aside.
export const GFX_FORCED = typeof document !== "undefined" && (param("gfx") === "1" || param("gfx") === "2");

// ?trees=model|baked|voxel pins every theater's trees: the screenshot
// director shoots the desktop look under software GL, and anyone can walk a
// phone's baked woods (or the old voxel ones) on a GPU. Null leaves it to the
// machine.
export const TREES_FORCED = typeof document === "undefined"
  ? null
  : ["model", "baked", "voxel"].includes(param("trees")) ? param("trees") : null;

// ?water=lite|full|hq pins how much sea gets drawn (water.js WATER_QUALITY),
// for the screenshot director and for pricing the sea on one machine. Null
// leaves it to the tier and the machine.
export const WATER_FORCED = typeof document === "undefined"
  ? null
  : ["lite", "full", "hq"].includes(param("water")) ? param("water") : null;

// ?fx=lite|full pins the effect flipbooks (flipbooks.js): the half-size atlas
// sampled one frame a card, or the full one blended frame to frame. The
// screenshot director shoots the desktop's under software GL; null leaves it
// to the machine (phones and software GL go lite).
export const FX_FORCED = typeof document === "undefined"
  ? null
  : param("fx") === "lite" || param("fx") === "full" ? param("fx") : null;

// ?gov=on|off pins the frame-time governor (governor.js): on to watch it work
// on a desktop (the probes run it under a CPU throttle), off to price a phone
// without it. ?gov=0..4 pins it AT that many rungs and stops it adapting, so a
// probe can price one rung against the next on any machine. Null leaves it to
// the machine: touch devices run it.
export const GOVERNOR_FORCED = typeof document === "undefined"
  ? null
  : param("gov") === "on" || param("gov") === "off" || /^[0-4]$/.test(param("gov") ?? "") ? param("gov") : null;

// Software rasterizers — SwiftShader when a GPU is blocklisted, llvmpipe in
// a VM, Windows' Basic Render Driver — draw every triangle on the CPU. The
// classic look already runs single-digit frames there; forests of model
// trees or a second render pass would stop it outright. main.js reads the
// renderer once and tells us (setSoftwareRenderer).
let softwareGL = false;

/** True when `gl`'s renderer is a CPU rasterizer rather than a GPU. */
export function isSoftwareRenderer(gl) {
  try {
    const dbg = gl?.getExtension("WEBGL_debug_renderer_info");
    const name = dbg ? gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) : gl?.getParameter(gl.RENDERER);
    return /swiftshader|llvmpipe|softpipe|software|basic render/i.test(String(name ?? ""));
  } catch {
    return false;
  }
}

export function setSoftwareRenderer(on) {
  softwareGL = !!on;
}

export function softwareRenderer() {
  return softwareGL;
}

/** Whether this device may pick tier 2 at all (desktop GPUs only). */
export function gfxSelectable() {
  try {
    return !softwareGL && !matchMedia("(pointer: coarse)").matches;
  } catch {
    return false;
  }
}

/** Save the preferred tier; it applies from the next page load. */
export function saveGfxTier(tier) {
  try {
    localStorage.setItem("va-gfx", tier >= 2 ? "2" : "1");
  } catch {
    /* storage blocked: the choice lasts this page only */
  }
}
